// @ts-nocheck
import { Configuration } from '@/generated/api';

export interface Tag {
  id: string;
  name: string;
  description?: string;
}

/**
 * Service for handling tag-related operations
 */
export class TagsService {
  private baseURL: string;
  private cache: Tag[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'https://api.swarmind.ai';
  }

  /**
   * Get all available tags
   */
  async getAvailableTags(): Promise<Tag[]> {
    // Check cache first
    const now = Date.now();
    if (this.cache && now - this.cacheTimestamp < this.CACHE_DURATION) {
      return this.cache;
    }

    try {
      const response = await fetch(`${this.baseURL}/tags`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tags: ${response.status}`);
      }

      const data = await response.json();

      // Transform the API response to our Tag interface
      // The API returns an array of strings, so we use the string as both id and name
      const tags: Tag[] = data.map((tagString: string) => ({
        id: tagString,
        name: tagString,
        description: '',
      }));

      // Cache the results
      this.cache = tags;
      this.cacheTimestamp = now;

      return tags;
    } catch (error) {
      console.error('Error fetching tags:', error);
      // Return fallback tags for development
      return this.getFallbackTags();
    }
  }

  /**
   * Get fallback tags for development/testing
   */
  private getFallbackTags(): Tag[] {
    return [
      { id: 'text', name: 'Text' },
      { id: 'llm', name: 'LLM' },
      { id: 'image', name: 'Image' },
      { id: 'chat', name: 'Chat' },
      { id: 'code', name: 'Code' },
      { id: 'creative', name: 'Creative' },
      { id: 'analysis', name: 'Analysis' },
      { id: 'reasoning', name: 'Reasoning' },
      { id: 'vision', name: 'Vision' },
      { id: 'gemma', name: 'Gemma' },
      { id: 'stable-diffusion', name: 'Stable Diffusion' },
      { id: 'text-to-image', name: 'Text-to-Image' },
    ];
  }

  /**
   * Clear the tags cache to force a fresh fetch
   */
  clearCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
  }
}

// Global singleton instance
let tagsServiceInstance: TagsService | null = null;

export const tagsService = (() => {
  if (!tagsServiceInstance) {
    tagsServiceInstance = new TagsService();
  }
  return tagsServiceInstance;
})();
